import React, { useEffect, useState } from "react"
import { Card, CardBody, Row, Col, Table, Button, Input, Label, Badge } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import "../Tenancy/tenancy.css"
import { getDocumentTypes, saveDocumentType, deleteDocumentType } from "../../services/admin"

const MASTERS = ["employee", "customer", "supplier", "sponsor", "company", "group"]
const MASTER_LABELS = {
  employee: "Employee", customer: "Customer", supplier: "Supplier",
  sponsor: "Sponsor", company: "Company", group: "Group",
}
const FIELD_TYPES = ["text", "number", "date", "select", "file", "boolean", "textarea"]

const slug = s => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
const blankField = () => ({ key: "", label: "", type: "text", is_required: false, is_expiry: false, options_text: "", file_types: "", help: "" })
const blank = {
  key: "", name: "", description: "", masters: [],
  tracks_expiry: false, notify_enabled: true, notify_before_days: "",
  is_active: true, sort_order: 0, fields: [blankField()],
}

const parseOptions = text =>
  (text || "").split("\n").map(l => l.trim()).filter(Boolean).map(l => {
    const [value, ...rest] = l.split("|")
    return { value: value.trim(), label: (rest.join("|") || value).trim() }
  })

/**
 * Developer > Documents Manager — define document types (a set of fields activated on
 * one or more masters) with optional expiry tracking + notifications. Inline two-card
 * layout (form + listing); mirrors StaffFlow's Documents Manager, styled for erpflow.
 */
const DocumentTypes = () => {
  document.title = "Documents Manager | Developer"
  const [rows, setRows] = useState([])
  const [reload, setReload] = useState(0)
  const [editingId, setEditingId] = useState(null)
  const [keyTouched, setKeyTouched] = useState(false)
  const [form, setForm] = useState({ ...blank })

  useEffect(() => {
    ;(async () => {
      try { const r = await getDocumentTypes(); setRows(r?.data || []) }
      catch (e) { toastError(e?.message) }
    })()
  }, [reload])

  // Auto-fill key from name until the user edits the key (add mode only).
  useEffect(() => {
    if (keyTouched || editingId) return
    setForm(f => ({ ...f, key: slug(f.name) }))
  }, [form.name, keyTouched, editingId])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const toggleMaster = m => setForm(f => ({ ...f, masters: f.masters.includes(m) ? f.masters.filter(x => x !== m) : [...f.masters, m] }))

  // Only one field may be the expiry date.
  const setField = (i, patch) => setForm(f => ({
    ...f,
    fields: f.fields.map((fl, idx) => {
      if (idx !== i) return patch.is_expiry ? { ...fl, is_expiry: false } : fl
      return { ...fl, ...patch }
    }),
  }))
  const addField = () => setForm(f => ({ ...f, fields: [...f.fields, blankField()] }))
  const removeField = i => setForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }))

  const startEdit = t => {
    setEditingId(t.id)
    setKeyTouched(true)
    window.scrollTo({ top: 0, behavior: "smooth" })
    setForm({
      key: t.key, name: t.name, description: t.description || "",
      masters: [...(t.masters || [])],
      tracks_expiry: !!t.tracks_expiry, notify_enabled: !!t.notify_enabled,
      notify_before_days: t.notify_before_days ?? "",
      is_active: !!t.is_active, sort_order: t.sort_order ?? 0,
      fields: (t.fields || []).length ? t.fields.map(f => ({
        key: f.key, label: f.label, type: f.type,
        is_required: !!f.is_required, is_expiry: !!f.is_expiry,
        options_text: (f.options || []).map(o => `${o.value}|${o.label}`).join("\n"),
        file_types: f.file_types || "", help: f.help || "",
      })) : [blankField()],
    })
  }

  const cancel = () => { setEditingId(null); setKeyTouched(false); setForm({ ...blank, fields: [blankField()] }) }

  const submit = async e => {
    e.preventDefault()
    const payload = {
      key: form.key,
      name: form.name,
      description: form.description,
      masters: form.masters,
      tracks_expiry: form.tracks_expiry,
      notify_enabled: form.notify_enabled,
      notify_before_days: form.notify_before_days === "" ? null : Number(form.notify_before_days),
      is_active: form.is_active,
      sort_order: form.sort_order,
      fields: form.fields.map((f, i) => ({
        key: f.key || slug(f.label),
        label: f.label,
        type: f.type,
        is_required: f.is_required,
        is_expiry: f.type === "date" ? f.is_expiry : false,
        file_types: f.type === "file" ? (f.file_types || null) : null,
        help: f.help || null,
        options: f.type === "select" ? parseOptions(f.options_text) : null,
        sort_order: i,
      })),
    }
    try {
      await saveDocumentType(editingId, payload)
      toastSuccess(editingId ? "Document updated" : "Document added")
      cancel()
      setReload(r => r + 1)
    } catch (err) {
      toastError(err?.message || (err?.errors && Object.values(err.errors)[0][0]) || "Save failed")
    }
  }

  const remove = async t => {
    if (!window.confirm(`Delete document “${t.name}”? Existing records are kept.`)) return
    try { await deleteDocumentType(t.id); toastSuccess("Deleted"); if (editingId === t.id) cancel(); setReload(r => r + 1) }
    catch (e) { toastError(e?.message) }
  }

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <Breadcrumbs
          title="Developer"
          breadcrumbObject={[
            { title: "Developer", link: "/developer" },
            { title: "Documents Manager", link: "#" },
          ]}
        />

        {/* ── Add / Edit form ───────────────────────────────────────────── */}
        <Card className="mb-3">
          <CardBody>
            <h5 className="mb-1">{editingId ? "Edit document" : "Add a document"}</h5>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              A document is a set of fields activated on one or more masters (Employee / Customer /
              Supplier / Sponsor / Company / Group). Mark one date field as the expiry to drive alerts.
            </p>
            <form onSubmit={submit} noValidate>
              {/* Identity */}
              <Row>
                <Col md={4}>
                  <Label className="dm-label">Name<span className="text-danger"> *</span></Label>
                  <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Trade Licence" />
                </Col>
                <Col md={4}>
                  <Label className="dm-label">Key<span className="text-danger"> *</span> <span className="text-muted">— {editingId ? "fixed" : "auto from name"}</span></Label>
                  <Input value={form.key} disabled={!!editingId} onChange={e => { setKeyTouched(true); set("key", e.target.value) }} placeholder="trade_licence" style={{ fontFamily: "monospace" }} />
                </Col>
                <Col md={4}>
                  <Label className="dm-label">Description</Label>
                  <Input value={form.description} onChange={e => set("description", e.target.value)} />
                </Col>
              </Row>

              {/* Masters */}
              <div className="mt-3">
                <Label className="dm-label">Activate on masters<span className="text-danger"> *</span></Label>
                <div className="d-flex flex-wrap" style={{ gap: "1rem" }}>
                  {MASTERS.map(m => (
                    <label key={m} className="d-flex align-items-center mb-0" style={{ gap: 6, cursor: "pointer", fontSize: 14 }}>
                      <Input type="checkbox" className="m-0" checked={form.masters.includes(m)} onChange={() => toggleMaster(m)} />
                      {MASTER_LABELS[m]}
                    </label>
                  ))}
                </div>
              </div>

              {/* Expiry / notify */}
              <Row className="mt-3 align-items-end">
                <Col md={3}>
                  <label className="d-flex align-items-center mb-0" style={{ gap: 6, cursor: "pointer", fontSize: 14 }}>
                    <Input type="checkbox" className="m-0" checked={form.tracks_expiry} onChange={e => set("tracks_expiry", e.target.checked)} /> Tracks expiry
                  </label>
                </Col>
                <Col md={3}>
                  <label className="d-flex align-items-center mb-0" style={{ gap: 6, cursor: "pointer", fontSize: 14 }}>
                    <Input type="checkbox" className="m-0" checked={form.notify_enabled} onChange={e => set("notify_enabled", e.target.checked)} /> Notify on expiry
                  </label>
                </Col>
                <Col md={3}>
                  <Label className="dm-label">Notify lead (days) <span className="text-muted">— blank = global</span></Label>
                  <Input type="number" min="1" max="365" value={form.notify_before_days} onChange={e => set("notify_before_days", e.target.value)} placeholder="e.g. 30" />
                </Col>
                <Col md={3}>
                  <label className="d-flex align-items-center mb-0" style={{ gap: 6, cursor: "pointer", fontSize: 14 }}>
                    <Input type="checkbox" className="m-0" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} /> Active
                  </label>
                </Col>
              </Row>

              {/* Fields */}
              <div className="border rounded mt-3">
                <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
                  <span className="dm-label mb-0">Fields</span>
                  <Button type="button" size="sm" color="light" className="border" onClick={addField}>+ Add field</Button>
                </div>
                <div>
                  {form.fields.map((f, i) => (
                    <div key={i} className="px-3 py-2 border-bottom">
                      <Row className="align-items-center g-2">
                        <Col md={3}><Input bsSize="sm" value={f.label} onChange={e => setField(i, { label: e.target.value, key: f.key || slug(e.target.value) })} placeholder="Label *" /></Col>
                        <Col md={2}><Input bsSize="sm" value={f.key} onChange={e => setField(i, { key: e.target.value })} placeholder="key" style={{ fontFamily: "monospace" }} /></Col>
                        <Col md={2}>
                          <Input bsSize="sm" type="select" value={f.type} onChange={e => setField(i, { type: e.target.value, ...(e.target.value !== "date" ? { is_expiry: false } : {}) })}>
                            {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </Input>
                        </Col>
                        <Col md={2}>
                          <label className="d-flex align-items-center mb-0" style={{ gap: 4, fontSize: 12 }}>
                            <Input type="checkbox" className="m-0" checked={f.is_required} onChange={e => setField(i, { is_required: e.target.checked })} /> Required
                          </label>
                        </Col>
                        <Col md={2}>
                          {f.type === "date" && (
                            <label className="d-flex align-items-center mb-0" style={{ gap: 4, fontSize: 12 }}>
                              <Input type="checkbox" className="m-0" checked={f.is_expiry} onChange={e => setField(i, { is_expiry: e.target.checked })} /> Expiry date
                            </label>
                          )}
                        </Col>
                        <Col md={1} className="text-end">
                          <Button type="button" size="sm" color="link" className="text-danger p-0" onClick={() => removeField(i)} disabled={form.fields.length === 1} title="Remove field">
                            <i className="uil-trash-alt" />
                          </Button>
                        </Col>
                      </Row>
                      {f.type === "select" && (
                        <Input className="mt-2" bsSize="sm" type="textarea" rows={2} value={f.options_text} onChange={e => setField(i, { options_text: e.target.value })} placeholder="One option per line:  value|Label" />
                      )}
                      {f.type === "file" && (
                        <Input className="mt-2" bsSize="sm" value={f.file_types} onChange={e => setField(i, { file_types: e.target.value })} placeholder="Allowed file types, comma-separated — e.g. pdf, jpg, png  (blank = default)" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
                {editingId && <Button type="button" color="light" className="border" onClick={cancel}>Cancel</Button>}
                <Button type="submit" color="primary">{editingId ? "Save changes" : "Add document"}</Button>
              </div>
            </form>
          </CardBody>
        </Card>

        {/* ── Listing ───────────────────────────────────────────────────── */}
        <Card>
          <CardBody>
            <h5 className="mb-3">Documents ({rows.length})</h5>
            <Table responsive borderless className="align-middle tnt-table mb-0">
              <thead>
                <tr><th>Document</th><th>Masters</th><th>Fields</th><th>Expiry</th><th>Status</th><th className="text-end">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id}>
                    <td><span className="fw-medium">{t.name}</span> <span className="text-muted" style={{ fontFamily: "monospace", fontSize: 12 }}>{t.key}</span></td>
                    <td>{(t.masters || []).map(m => <Badge key={m} color="light" className="text-dark border me-1">{MASTER_LABELS[m] || m}</Badge>)}</td>
                    <td className="text-muted">{(t.fields || []).length}</td>
                    <td>{t.tracks_expiry ? <Badge color="info" className="bg-opacity-10 text-info">tracked</Badge> : <span className="text-muted">—</span>}</td>
                    <td>{t.is_active ? <Badge color="success">active</Badge> : <Badge color="secondary">inactive</Badge>}</td>
                    <td className="text-end text-nowrap">
                      <i className="uil-pen action-icon" role="button" title="Edit" onClick={() => startEdit(t)} />
                      <i className="uil-trash-alt action-icon danger" role="button" title="Delete" onClick={() => remove(t)} />
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan="6" className="text-center text-muted py-4">No documents yet — add one above.</td></tr>}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default DocumentTypes
